import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
import { Button } from "react-aria-components";
import checkIcon from "@/assets/figma/chat-workflow/check.svg";
import loadIcon from "@/assets/figma/chat-workflow/load.svg";
import { ConversationMediaGroups } from "./ConversationMediaGroup";
import type { ConversationMediaSlot } from "./conversationMediaOwnership";
import { creativeMediaElapsedMs, creativeMediaStatusLabels, isCreativeMediaRunnable } from "./creativeMediaProgress";
import "./creativeMediaProgress.css";

interface Props {
  slots: readonly ConversationMediaSlot[];
  conversationId: string;
  organizationId: string;
  shopBindingId: string;
  monotonicNow: number;
  stale: boolean;
  onReadAgain(): void;
  onOpenCanvas(jobId: string, resourceId?: string): void;
}

export function ConversationCreativeMediaProgress(props: Props) {
  return props.slots.map((slot) => <CreativeMediaProgressCard {...props} key={slot.jobId} slot={slot} />);
}

function CreativeMediaProgressCard({ slot, conversationId, organizationId, shopBindingId, monotonicNow, stale, onReadAgain, onOpenCanvas }: Props & { slot: ConversationMediaSlot }) {
  const root = useRef<HTMLElement>(null);
  const job = slot.sample?.job;
  const kind = job?.media_kind ?? slot.mediaKind;
  const title = kind === "video" ? "视频生成" : kind === "image" ? "图片生成" : "媒体生成";
  const active = job ? isCreativeMediaRunnable(job.status) : false;
  const elapsed = slot.sample ? creativeMediaElapsedMs(slot.sample, monotonicNow) : null;
  useGSAP(() => {
    if (!root.current) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    gsap.fromTo(root.current, { opacity: reduced ? 1 : 0.65 }, { opacity: 1, duration: reduced ? 0 : 0.18, overwrite: true });
  }, { dependencies: [job?.status], scope: root });
  return <section aria-label={`${title}任务`}
    className="conversation-creative-media-progress" data-media-job-id={slot.jobId}
    data-media-status={job?.status ?? "reading"} data-media-stale={stale} ref={root}>
    {job || slot.parts.length === 0 ? <div className="conversation-creative-media-progress__summary">
      {job?.status === "succeeded" ? <img alt="" src={checkIcon} /> :
        active && !stale ? <img alt="" className="conversation-creative-media-progress__spinner" src={loadIcon} /> : null}
      <strong>{title}</strong>
      <span role="status">{stale ? "状态暂未同步" : job ? creativeMediaStatusLabels[job.status] : "正在读取任务状态"}</span>
      {elapsed !== null ? <span className="conversation-creative-media-progress__elapsed">{formatElapsed(elapsed)}<small>含排队</small></span> : null}
    </div> : null}
    {job?.error ? <p className="conversation-creative-media-progress__notice">{job.error.message}</p> : null}
    {job?.status === "succeeded" && slot.parts.length === 0 ? <p className="conversation-creative-media-progress__notice">
      任务已完成，结果暂不可读取。<Button onPress={onReadAgain}>重新读取结果</Button>
    </p> : null}
    {slot.parts.length > 0 ? <ConversationMediaGroups conversationId={conversationId} organizationId={organizationId}
      onOpenCanvas={onOpenCanvas} parts={slot.parts} shopBindingId={shopBindingId} /> : null}
  </section>;
}

function formatElapsed(milliseconds: number) {
  const seconds = Math.floor(milliseconds / 1000);
  return seconds < 60 ? `${seconds}秒` : `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
}
